/**
 * P3.2 — Commitments table.
 *
 * Multi-cloud table for managing customer commitments (RI / SP / CUD / Reserved VM).
 * Three flows the operator drives from this UI:
 *   1. Sync from cloud API — POST /commitments/sync triggers T1 fetch
 *      (whichever cloud the cluster lives on). On success, T1 rows appear
 *      with a "Live API" source pill.
 *   2. Add manual entry (T4) — modal form with cloud picker, kind, scope,
 *      coverage, expiration. Stored separately from API-fetched rows.
 *   3. Delete user-entered rows (API-fetched rows can't be deleted; they
 *      refresh on next sync).
 *
 * Source pills make the data lineage explicit — "AWS Cost Explorer (Live)"
 * vs "Manual entry" — so operators see which they're acting on.
 *
 * Expiring-soon highlight: rows expiring in ≤30d render in amber, ≤7d in red.
 *
 * Backend routes:
 *   GET    /api/v1/clusters/{id}/commitments
 *   POST   /api/v1/clusters/{id}/commitments
 *   DELETE /api/v1/clusters/{id}/commitments/{commit_id}
 *   POST   /api/v1/clusters/{id}/commitments/sync
 *   GET    /api/v1/clusters/{id}/commitments/expiring
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, Plus, Trash2, RefreshCw, AlertCircle, TrendingDown, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
const SOURCE_PILLS = {
    aws_cost_explorer: { label: 'AWS Cost Explorer (Live)', color: 'bg-blue-100 text-blue-800' },
    gcp_billing_api:   { label: 'GCP Billing API (Live)',   color: 'bg-emerald-100 text-emerald-800' },
    azure_cost_mgmt:   { label: 'Azure Cost Mgmt (Live)',   color: 'bg-sky-100 text-sky-800' },
    cur_export:        { label: 'AWS CUR Export',            color: 'bg-blue-50 text-blue-700' },
    gcp_bq_export:     { label: 'GCP BQ Export',             color: 'bg-emerald-50 text-emerald-700' },
    azure_cost_export: { label: 'Azure Cost Export',         color: 'bg-sky-50 text-sky-700' },
    opencost:          { label: 'OpenCost',                  color: 'bg-purple-100 text-purple-800' },
    user_input:        { label: 'Manual entry',              color: 'bg-gray-100 text-gray-800' },
};

const CLOUD_OPTIONS = [
    { value: 'aws', label: 'AWS' },
    { value: 'gcp', label: 'GCP' },
    { value: 'azure', label: 'Azure' },
];

const KIND_OPTIONS = [
    { value: 'reserved', label: 'Reserved (RI / RVI)' },
    { value: 'savings_plan', label: 'Savings Plan' },
    { value: 'committed_use', label: 'Committed Use Discount' },
];

const SCOPE_OPTIONS = [
    { value: 'instance_type', label: 'Specific instance type' },
    { value: 'family', label: 'Instance family' },
    { value: 'region', label: 'Whole region' },
    { value: 'account', label: 'Account-wide' },
];

const daysUntil = (iso) => {
    if (!iso) return null;
    const exp = parseUtc(iso);
    if (isNaN(exp.getTime())) return null;
    return Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const expirationStyle = (iso) => {
    const d = daysUntil(iso);
    if (d == null) return '';
    if (d < 0) return 'text-gray-400 line-through';
    if (d <= 7) return 'text-red-700 font-semibold';
    if (d <= 30) return 'text-amber-700';
    return 'text-gray-700';
};

const SourcePill = ({ source }) => {
    const cfg = SOURCE_PILLS[source] || { label: source, color: 'bg-gray-50 text-gray-600' };
    return (
        <span className={`inline-block px-2 py-0.5 text-xs rounded ${cfg.color}`}>
            {cfg.label}
        </span>
    );
};

// ── shared design-system primitives (match Section/StatusPill in the app) ──
const usd = (v, d = 0) =>
    (v == null || Number.isNaN(v)) ? '—'
        : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;

const Chip = ({ children, tone = 'slate' }) => {
    const tones = {
        slate: 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300',
        emerald: 'border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400',
        amber: 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone] || tones.slate}`}>
            {children}
        </span>
    );
};

export default function CommitmentsTable({ clusterId }) {
    const [data, setData] = useState({ commitments: [], total: 0, by_source: {} });
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [expiring, setExpiring] = useState([]);
    const [recs, setRecs] = useState(null); // WS-E Lever 3: buy/extend recommendations

    const fetchAll = useCallback(async () => {
        if (!clusterId) return;
        try {
            const [resp, expResp, recResp] = await Promise.all([
                axios.get(`${API}/v1/clusters/${clusterId}/commitments`),
                axios.get(`${API}/v1/clusters/${clusterId}/commitments/expiring`, {
                    params: { days: 30 },
                }),
                axios.get(`${API}/v1/clusters/${clusterId}/commitments/recommendations`, {
                    params: { term_months: 12, target_coverage_pct: 0.8 },
                }).catch(() => ({ data: null })), // recs are additive — never block the table
            ]);
            setData(resp.data);
            setExpiring(expResp.data.expiring || []);
            setRecs(recResp.data);
        } catch (err) {
            // Empty state is fine — no toast needed
        } finally {
            setLoading(false);
        }
    }, [clusterId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const resp = await axios.post(`${API}/v1/clusters/${clusterId}/commitments/sync`);
            if (resp.data.source_used) {
                toast.success(
                    `Synced ${resp.data.commitments_fetched} from ${SOURCE_PILLS[resp.data.source_used]?.label || resp.data.source_used}`
                );
            } else {
                toast.info('No cloud-API data available — add manual entries below');
            }
            await fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleDelete = async (commitId) => {
        try {
            await axios.delete(`${API}/v1/clusters/${clusterId}/commitments/${commitId}`);
            toast.success('Commitment removed');
            await fetchAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Delete failed');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8 text-slate-400 dark:text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading commitments…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header + source-pill row + actions */}
            <div className="flex items-end justify-between flex-wrap gap-3 border-b border-slate-200/80 dark:border-slate-800 pb-3">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
                        Rate-side savings
                    </div>
                    <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                        Your coverage
                    </h3>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap items-center">
                        {Object.entries(data.by_source || {}).map(([src, n]) => (
                            <span key={src} className="inline-flex items-center gap-1">
                                <SourcePill source={src} />
                                <span className="text-[11px] text-slate-400">×{n}</span>
                            </span>
                        ))}
                        {data.total === 0 && (
                            <span className="text-xs text-slate-400 dark:text-slate-500">
                                No active commitments on file yet.
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleSync}
                        disabled={syncing}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
                    >
                        {syncing
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <RefreshCw className="w-4 h-4" />}
                        Sync cloud API
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 dark:bg-slate-100 px-3 py-1.5 text-sm font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white transition-colors"
                    >
                        <Plus className="w-4 h-4" /> Add manual
                    </button>
                </div>
            </div>

            {/* Savings opportunities (WS-E Lever 3): cover the always-on baseline
                spot can't reach — the rate-side lever. */}
            {recs && recs.recommendations && recs.recommendations.length > 0 && (
                <div className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                    {/* KPI header */}
                    <div className="flex items-center justify-between gap-4 border-b border-slate-200/80 dark:border-slate-800 px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-950/50">
                                <TrendingDown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">
                                    Savings opportunities
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    {recs.recommendations.length} way{recs.recommendations.length === 1 ? '' : 's'} to cover your always-on baseline
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums leading-none">
                                {usd(recs.total_estimated_monthly_savings)}
                                <span className="text-sm font-normal text-slate-400">/mo</span>
                            </div>
                            <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                                potential savings{recs.edp_discount_pct > 0 ? ` · net of ${(recs.edp_discount_pct * 100).toFixed(0)}% EDP` : ''}
                            </div>
                        </div>
                    </div>

                    {/* Opportunity rows */}
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {recs.recommendations.slice(0, 6).map((r, i) => {
                            const opts = Array.isArray(r.purchase_options) ? r.purchase_options : [];
                            const best = opts.reduce((a, b) => (b.est_monthly_savings > (a?.est_monthly_savings ?? -1) ? b : a), null);
                            return (
                                <div key={i} className="px-4 py-3.5">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.action === 'extend' ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400' : 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400'}`}>
                                                    {r.action === 'extend' ? 'Extend' : 'Buy'}
                                                </span>
                                                <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                                                    {r.scope_value}
                                                </span>
                                                {r.region && (
                                                    <span className="text-xs text-slate-400 font-mono">{r.region}</span>
                                                )}
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                <Chip>{r.recommended_cover_units?.toFixed(1)} of {r.steady_state_units?.toFixed(1)} always-on units</Chip>
                                                {typeof r.break_even_utilization_pct === 'number' && (
                                                    <Chip tone="emerald">break-even {(r.break_even_utilization_pct * 100).toFixed(0)}% util</Chip>
                                                )}
                                                {typeof r.confidence === 'number' && (
                                                    <Chip>{(r.confidence * 100).toFixed(0)}% confidence</Chip>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-lg font-semibold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums leading-none">
                                                {usd(r.est_monthly_savings)}<span className="text-xs font-normal text-slate-400">/mo</span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-slate-400">{r.term_months}-mo term</div>
                                        </div>
                                    </div>

                                    {/* Purchase-option spread */}
                                    {opts.length > 0 && (
                                        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-100 dark:border-slate-800">
                                            <table className="w-full text-xs tabular-nums">
                                                <thead>
                                                    <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50/70 dark:bg-slate-800/40">
                                                        <th className="px-3 py-2 font-semibold">Option</th>
                                                        <th className="px-3 py-2 font-semibold text-right">Savings/mo</th>
                                                        <th className="px-3 py-2 font-semibold text-right">Off</th>
                                                        <th className="px-3 py-2 font-semibold text-right">Upfront</th>
                                                        <th className="px-3 py-2 font-semibold text-right">$/hr</th>
                                                        <th className="px-3 py-2 font-semibold">Flexibility</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                    {opts.map((o, j) => {
                                                        const isBest = best && o.label === best.label;
                                                        return (
                                                            <tr key={j} className={isBest ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''}>
                                                                <td className="px-3 py-2 text-slate-700 dark:text-slate-200">
                                                                    <span className="inline-flex items-center gap-1.5">
                                                                        {o.label}
                                                                        {isBest && <span className="rounded bg-emerald-100 dark:bg-emerald-900/60 px-1.5 py-px text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">best value</span>}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-slate-100">{usd(o.est_monthly_savings)}</td>
                                                                <td className="px-3 py-2 text-right text-slate-500 dark:text-slate-400">{(o.discount_pct * 100).toFixed(0)}%</td>
                                                                <td className="px-3 py-2 text-right text-slate-500 dark:text-slate-400">{o.upfront_cost > 0 ? usd(o.upfront_cost) : '—'}</td>
                                                                <td className="px-3 py-2 text-right text-slate-500 dark:text-slate-400">{o.hourly_commitment_usd != null ? `$${o.hourly_commitment_usd.toFixed(3)}` : '—'}</td>
                                                                <td className="px-3 py-2 text-slate-400 dark:text-slate-500">{o.flexibility}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2.5 text-[11px] text-slate-400 dark:text-slate-500">
                        Conservative public-rate estimates. Once you purchase, add it under <span className="font-medium text-slate-500 dark:text-slate-400">Add manual</span> so the planner stops counting that baseline as on-demand.
                    </div>
                </div>
            )}
            {recs && (!recs.recommendations || recs.recommendations.length === 0) && recs.note && (
                <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 px-4 py-6 text-center">
                    <div className="text-sm font-medium text-slate-600 dark:text-slate-300">No savings opportunities yet</div>
                    <p className="mx-auto mt-1 max-w-md text-xs text-slate-400 dark:text-slate-500">{recs.note}.</p>
                </div>
            )}

            {/* Expiring-soon banner */}
            {expiring.length > 0 && (
                <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/30 p-4 flex items-start gap-3">
                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm min-w-0">
                        <div className="font-semibold text-amber-800 dark:text-amber-300">
                            {expiring.length} commitment{expiring.length === 1 ? '' : 's'} expiring within 30 days
                        </div>
                        <ul className="mt-1.5 space-y-1 text-amber-700 dark:text-amber-400/90">
                            {expiring.slice(0, 5).map((e, i) => (
                                <li key={i} className="flex items-center gap-2">
                                    <span className="font-mono text-xs">{e.scope_value}</span>
                                    <Chip tone="amber">{e.days_left} day{e.days_left === 1 ? '' : 's'} left</Chip>
                                    {e.monthly_committed_spend > 0 && (
                                        <span className="text-xs text-amber-600/80 dark:text-amber-400/70 tabular-nums">
                                            {usd(e.monthly_committed_spend, 2)}/mo
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Existing coverage table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-800">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200/80 dark:border-slate-800">
                            <th className="px-3 py-2.5">Cloud</th>
                            <th className="px-3 py-2.5">Kind</th>
                            <th className="px-3 py-2.5">Scope</th>
                            <th className="px-3 py-2.5 text-right">Coverage</th>
                            <th className="px-3 py-2.5 text-right">Spend/mo</th>
                            <th className="px-3 py-2.5">Expires</th>
                            <th className="px-3 py-2.5">Source</th>
                            <th className="px-3 py-2.5"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {data.commitments.length === 0 && (
                            <tr><td colSpan={8} className="text-center px-3 py-8 text-slate-400 dark:text-slate-500">
                                No active commitments — use <span className="font-medium text-slate-500">Sync cloud API</span> or <span className="font-medium text-slate-500">Add manual</span>.
                            </td></tr>
                        )}
                        {data.commitments.map((c) => (
                            <tr key={c.id || `${c.scope}-${c.scope_value}-${c.commitment_data_source}`}
                                className="text-slate-700 dark:text-slate-200 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                                <td className="px-3 py-2.5 uppercase font-mono text-xs text-slate-500">{c.cloud_provider}</td>
                                <td className="px-3 py-2.5">{c.commitment_kind}</td>
                                <td className="px-3 py-2.5 font-mono">
                                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{c.scope}</div>
                                    <div className="text-slate-800 dark:text-slate-100">{c.scope_value}</div>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                                    {Math.round((c.coverage_pct || 0) * 100)}%
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                                    {usd(c.monthly_committed_spend || 0, 2)}
                                </td>
                                <td className={`px-3 py-2.5 tabular-nums ${expirationStyle(c.expires_at)}`}>
                                    {c.expires_at || '—'}
                                </td>
                                <td className="px-3 py-2.5">
                                    <SourcePill source={c.commitment_data_source} />
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                    {c.commitment_data_source === 'user_input' && c.id && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(c.id)}
                                            title="Delete this manual entry"
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showAddModal && (
                <AddCommitmentModal
                    clusterId={clusterId}
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); fetchAll(); }}
                />
            )}
        </div>
    );
}


function AddCommitmentModal({ clusterId, onClose, onSaved }) {
    const [form, setForm] = useState({
        cloud_provider: 'aws',
        commitment_kind: 'reserved',
        scope: 'instance_type',
        scope_value: '',
        region: '',
        coverage_pct: 1.0,
        monthly_committed_spend: 0,
        effective_hourly_rate: 0,
        on_demand_hourly_rate: 0,
        commitment_term: '1yr_no_upfront',
        commitment_term_months: 12,
        expires_at: '',
        note: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/v1/clusters/${clusterId}/commitments`, {
                ...form,
                expires_at: form.expires_at || null,
                coverage_pct: parseFloat(form.coverage_pct),
                monthly_committed_spend: parseFloat(form.monthly_committed_spend),
                effective_hourly_rate: parseFloat(form.effective_hourly_rate),
                on_demand_hourly_rate: parseFloat(form.on_demand_hourly_rate),
                commitment_term_months: parseInt(form.commitment_term_months, 10),
            });
            toast.success('Commitment added');
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded shadow-lg max-w-lg w-full p-6 space-y-4">
                <h3 className="text-lg font-semibold">Add commitment (manual)</h3>
                <p className="text-xs text-gray-500">
                    Use this when your cloud account's IAM grant for OptOps doesn't
                    include commitment read. T1 cloud-API entries take precedence
                    when later available.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Cloud">
                        <select value={form.cloud_provider}
                                onChange={(e) => update('cloud_provider', e.target.value)}
                                className="w-full border rounded px-2 py-1">
                            {CLOUD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Kind">
                        <select value={form.commitment_kind}
                                onChange={(e) => update('commitment_kind', e.target.value)}
                                className="w-full border rounded px-2 py-1">
                            {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Scope">
                        <select value={form.scope}
                                onChange={(e) => update('scope', e.target.value)}
                                className="w-full border rounded px-2 py-1">
                            {SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Scope value (e.g. m5.large)">
                        <input value={form.scope_value} required
                               onChange={(e) => update('scope_value', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                    <Field label="Region (optional)">
                        <input value={form.region}
                               onChange={(e) => update('region', e.target.value)}
                               className="w-full border rounded px-2 py-1"
                               placeholder="us-east-1" />
                    </Field>
                    <Field label="Coverage % (0.0 - 1.0)">
                        <input type="number" step="0.01" min="0" max="1"
                               value={form.coverage_pct}
                               onChange={(e) => update('coverage_pct', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                    <Field label="Monthly committed spend $">
                        <input type="number" step="0.01" min="0"
                               value={form.monthly_committed_spend}
                               onChange={(e) => update('monthly_committed_spend', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                    <Field label="Term (months)">
                        <input type="number" min="1" max="36"
                               value={form.commitment_term_months}
                               onChange={(e) => update('commitment_term_months', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                    <Field label="Expires at (YYYY-MM-DD)" full>
                        <input type="date" value={form.expires_at}
                               onChange={(e) => update('expires_at', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                    <Field label="Note (optional)" full>
                        <input value={form.note}
                               onChange={(e) => update('note', e.target.value)}
                               className="w-full border rounded px-2 py-1" />
                    </Field>
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose}
                            className="px-3 py-1.5 text-sm rounded border">
                        Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, full }) {
    return (
        <label className={`block ${full ? 'col-span-2' : ''}`}>
            <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
            {children}
        </label>
    );
}
